import React, { useState } from "react";
import { X, MessageCircle, Send, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Universal Contact Support entry point.
 *
 * Works for BOTH authenticated and unauthenticated visitors:
 *   - Signed-in customers auto-attach their email server-side (cookie-based).
 *   - Anonymous visitors must supply an email so we can reply.
 *
 * All tickets forward to Telegram via `POST /api/support/message` — same
 * pipeline used by the in-dashboard SupportBubble + /dashboard/support page.
 *
 * Render as either:
 *   <ContactSupportLink>Contact support →</ContactSupportLink>
 *     (an inline text link that opens the modal)
 *   <ContactSupportButton />
 *     (a full button with an icon)
 */

const CATEGORIES = [
  { value: "general",  label: "General question" },
  { value: "billing",  label: "Billing / plans / promo code" },
  { value: "tv",       label: "TV / Android stick" },
  { value: "editor",   label: "Design editor" },
  { value: "canva",    label: "Canva integration" },
  { value: "bug",      label: "Something is broken" },
];

function ContactSupportModal({ open, onClose, defaultCategory = "general", defaultSubject = "" }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState(defaultCategory);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null); // { ticket_id, delivered } | null

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!subject.trim() || !message.trim()) {
      toast.error("Please add a subject and a message.");
      return;
    }
    if (!email.trim()) {
      toast.error("Please add your email so we can reply.");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/support/message", {
        subject: subject.trim(),
        message: message.trim(),
        email: email.trim(),
        name: name.trim(),
        category,
        page_url: typeof window !== "undefined" ? window.location.href : "",
      });
      setSent(r.data);
      toast.success(`Ticket ${r.data.ticket_id} sent to support`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-3 sm:p-6" data-testid="contact-support-modal">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:w-[480px] max-h-[92vh] bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 bg-gradient-to-br from-sky-50 to-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-sky-500 text-white flex items-center justify-center shadow-md">
              <MessageCircle className="h-5 w-5" />
            </div>
            <div>
              <div className="text-base font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Create a support ticket</div>
              <div className="text-xs text-slate-500">Usually replies within 30 minutes</div>
            </div>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500" aria-label="Close" data-testid="contact-support-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {sent ? (
            <div className="text-center py-8" data-testid="contact-support-sent">
              <div className="h-14 w-14 rounded-full bg-emerald-100 text-emerald-600 mx-auto flex items-center justify-center mb-4">
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <div className="text-base font-semibold text-slate-900 mb-1">Ticket sent</div>
              <div className="text-xs text-slate-500 mb-4">
                Reference: <span className="font-mono font-semibold text-slate-800">{sent.ticket_id}</span>
              </div>
              <p className="text-sm text-slate-600 max-w-xs mx-auto leading-relaxed">
                We&apos;ll reply by email at <strong className="text-slate-800">{email}</strong>. Keep an eye on your inbox — replies usually arrive within 30 minutes during business hours.
              </p>
              <Button
                onClick={() => { setSent(null); setSubject(""); setMessage(""); }}
                variant="outline"
                className="mt-6 rounded-full h-9 text-xs"
                data-testid="contact-support-send-another"
              >
                Send another ticket
              </Button>
            </div>
          ) : (
            <form className="space-y-3" onSubmit={submit}>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Your name</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Baker"
                    className="mt-1 h-10 text-sm rounded-lg bg-slate-50 border-slate-200"
                    data-testid="contact-support-name"
                  />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Email *</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@business.com"
                    className="mt-1 h-10 text-sm rounded-lg bg-slate-50 border-slate-200"
                    required
                    data-testid="contact-support-email"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Category</Label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="mt-1 w-full h-10 text-sm rounded-lg bg-slate-50 border border-slate-200 px-3 focus:outline-none focus:ring-2 focus:ring-sky-500"
                  data-testid="contact-support-category"
                >
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Subject *</Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Short summary of what's wrong"
                  className="mt-1 h-10 text-sm rounded-lg bg-slate-50 border-slate-200"
                  required
                  data-testid="contact-support-subject"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Message *</Label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Describe your issue. Include screen names, error messages, and any steps you already tried."
                  className="mt-1 w-full min-h-[110px] text-sm rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500 resize-y"
                  required
                  data-testid="contact-support-message"
                />
              </div>
              <Button
                type="submit"
                disabled={sending}
                className="w-full rounded-full h-11 bg-sky-500 hover:bg-sky-600 text-white shadow-md"
                data-testid="contact-support-submit"
              >
                {sending ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
                ) : (
                  <><Send className="h-4 w-4 mr-2" /> Send ticket</>
                )}
              </Button>
              <p className="text-[10px] text-slate-400 text-center leading-relaxed">
                Tickets are forwarded to our on-call team via Telegram. Response times are typically under 30 minutes during business hours.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Inline text link that opens the ticket modal.
 * Usage: <ContactSupportLink>Contact support →</ContactSupportLink>
 */
export function ContactSupportLink({ children, className = "text-sky-600 hover:text-sky-700 underline underline-offset-2", category, subject, testId = "contact-support-link" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={className} data-testid={testId}>
        {children}
      </button>
      <ContactSupportModal open={open} onClose={() => setOpen(false)} defaultCategory={category} defaultSubject={subject} />
    </>
  );
}

/**
 * A full button-style contact support element (icon + label).
 */
export function ContactSupportButton({ label = "Contact support", className, category, subject, testId = "contact-support-button" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        className={className || "rounded-full bg-sky-50 border-sky-200 text-sky-700 hover:bg-sky-100"}
        data-testid={testId}
      >
        <MessageCircle className="h-4 w-4 mr-2" />
        {label}
      </Button>
      <ContactSupportModal open={open} onClose={() => setOpen(false)} defaultCategory={category} defaultSubject={subject} />
    </>
  );
}

export default ContactSupportLink;
